import React from 'react';

export function Pagination({
  page,
  totalPages,
  onPageChange,
  /** Render bar even when there is only one page (controls disabled). */
  alwaysShow = false,
  /** Total item count for “Showing X–Y of Z” summary. */
  total,
  pageSize,
  className = '',
}) {
  const safeTotalPages = Math.max(1, totalPages || 1);
  const safePage = Math.min(Math.max(1, page || 1), safeTotalPages);

  if (!alwaysShow && safeTotalPages <= 1) return null;

  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, safePage - Math.floor(maxVisible / 2));
  let end = Math.min(safeTotalPages, start + maxVisible - 1);

  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  const totalCount = typeof total === 'number' && Number.isFinite(total) ? total : null;
  const size = typeof pageSize === 'number' && pageSize > 0 ? pageSize : null;
  let rangeLabel = null;
  if (totalCount != null && size != null) {
    if (totalCount === 0) {
      rangeLabel = 'No results';
    } else {
      const rangeStart = (safePage - 1) * size + 1;
      const rangeEnd = Math.min(safePage * size, totalCount);
      rangeLabel = `Showing ${rangeStart}–${rangeEnd} of ${totalCount}`;
    }
  } else if (safeTotalPages > 1 || alwaysShow) {
    rangeLabel = `Page ${safePage} of ${safeTotalPages}`;
  }

  return (
    <nav
      aria-label="Pagination"
      className={`mt-6 border-t border-white/10 pt-4 ${className}`.trim()}
    >
      {rangeLabel ? (
        <p className="mb-3 text-center text-xs font-medium tabular-nums text-gray-500">{rangeLabel}</p>
      ) : null}
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(safePage - 1)}
          disabled={safePage <= 1}
          className="w-10 h-10 rounded-lg flex items-center justify-center border border-hash-gold/20 bg-dark-surface/50 text-hash-gold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-hash-gold/10 transition-colors cursor-pointer"
        >
          <img src="/images/icon-chevron-left.svg" alt="Prev" className="w-4 h-4" />
        </button>

        {start > 1 && (
          <>
            <button
              type="button"
              onClick={() => onPageChange(1)}
              className="w-10 h-10 rounded-lg flex items-center justify-center border border-hash-gold/20 bg-dark-surface/50 text-gray-300 hover:bg-hash-gold/10 transition-colors text-sm cursor-pointer"
            >
              1
            </button>
            {start > 2 && <span className="text-gray-500 px-1">...</span>}
          </>
        )}

        {pages.map((p) => (
          <button
            type="button"
            key={p}
            onClick={() => onPageChange(p)}
            className={`w-10 h-10 rounded-lg flex items-center justify-center border text-sm font-semibold transition-colors cursor-pointer ${
              p === safePage
                ? 'border-hash-gold bg-hash-gold/20 text-hash-gold'
                : 'border-hash-gold/20 bg-dark-surface/50 text-gray-300 hover:bg-hash-gold/10'
            }`}
          >
            {p}
          </button>
        ))}

        {end < safeTotalPages && (
          <>
            {end < safeTotalPages - 1 && <span className="text-gray-500 px-1">...</span>}
            <button
              type="button"
              onClick={() => onPageChange(safeTotalPages)}
              className="w-10 h-10 rounded-lg flex items-center justify-center border border-hash-gold/20 bg-dark-surface/50 text-gray-300 hover:bg-hash-gold/10 transition-colors text-sm cursor-pointer"
            >
              {safeTotalPages}
            </button>
          </>
        )}

        <button
          type="button"
          onClick={() => onPageChange(safePage + 1)}
          disabled={safePage >= safeTotalPages}
          className="w-10 h-10 rounded-lg flex items-center justify-center border border-hash-gold/20 bg-dark-surface/50 text-hash-gold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-hash-gold/10 transition-colors cursor-pointer"
        >
          <img src="/images/icon-chevron-right.svg" alt="Next" className="w-4 h-4" />
        </button>
      </div>
    </nav>
  );
}

export default Pagination;
